import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { FaSearch } from 'react-icons/fa';
import { UserItem } from '../components/UserItem';

export default function Followers() {
  const { userId } = useParams();
  const auth = getAuth();
  const [followers, setFollowers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  // Fetch user data and their followers
  useEffect(() => {
    const fetchFollowers = async () => {
      try {
        setIsLoading(true);

        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const user = userSnap.data();
          setUserData({
            name: user.name,
            username: user.username
          });

          // Handle both array and object formats for followers
          let followersArray = [];
          if (Array.isArray(user.followers)) {
            followersArray = user.followers;
          } else if (user.followers && typeof user.followers === 'object') {
            followersArray = Object.keys(user.followers);
          }

          if (followersArray.length === 0) {
            setFollowers([]);
            setIsLoading(false);
            return;
          }

          // Fetch each follower's data in batches
          const batchSize = 10;
          const batches = Math.ceil(followersArray.length / batchSize);
          let allFollowers = [];

          for (let i = 0; i < batches; i++) {
            const batch = followersArray.slice(i * batchSize, (i + 1) * batchSize);
            const followersQuery = query(
              collection(db, 'users'),
              where('__name__', 'in', batch)
            );
            const followersSnap = await getDocs(followersQuery);
            const batchData = followersSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            allFollowers = [...allFollowers, ...batchData];
          }

          setFollowers(allFollowers);
        }
      } catch (error) {
        console.error('Error fetching followers:', error);
        setFollowers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFollowers();
  }, [userId]);

  // Filter followers based on search query
  const filteredFollowers = followers.filter(follower => {
    if (!searchQuery.trim()) return true;
    const searchTerm = searchQuery.toLowerCase();
    return (
      (follower.name && follower.name.toLowerCase().includes(searchTerm)) ||
      (follower.username && follower.username.toLowerCase().includes(searchTerm))
    );
  });

  return (
    <div className="bg-black min-h-screen">
      {/* Header with back button and user info */}
      <div className="sticky top-0 z-10 bg-black bg-opacity-90 border-b border-gray-800 p-4">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => window.history.back()}
            className="text-white hover:text-gray-300"
          >
            ← Back
          </button>
          {userData && (
            <div className="text-center">
              <h1 className="text-white font-bold">{userData.name}</h1>
              <p className="text-gray-400 text-sm">@{userData.username}</p>
            </div>
          )}
          <div className="w-8"></div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="sticky top-16 z-10 bg-black bg-opacity-90 border-b border-gray-800 p-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Search followers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 text-white pl-10 pr-4 py-2 rounded-full focus:outline-none focus:border-[#FF1493]"
          />
        </div>
      </div>

      {/* Count and List */}
      <div className="pb-20">
        <div className="px-4 py-3 border-b border-gray-800">
          <h2 className="text-white font-semibold">
            {filteredFollowers.length} {filteredFollowers.length === 1 ? 'Follower' : 'Followers'}
          </h2>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center py-4 px-4">
                <div className="w-12 h-12 rounded-full bg-gray-700 animate-pulse mr-3"></div>
                <div className="flex-1">
                  <div className="h-4 w-24 bg-gray-700 rounded animate-pulse mb-1"></div>
                  <div className="h-3 w-16 bg-gray-700 rounded animate-pulse"></div>
                </div>
                <div className="h-8 w-20 bg-gray-700 rounded-full animate-pulse"></div>
              </div>
            ))}
          </div>
        ) : filteredFollowers.length > 0 ? (
          filteredFollowers.map((follower) => (
            <UserItem key={follower.id} userId={follower.id} />
          ))
        ) : (
          <div className="text-center py-10">
            <p className="text-gray-500">
              {searchQuery.trim() ? "No matching followers found" : "No followers yet"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}